import { action, computed, makeObservable, observable } from 'mobx'

import { getHashCode } from 'utils/helpers/getHashCode'

export abstract class Entity {
  @observable id: number | null = null

  constructor(entity: Entity) {
    makeObservable(this)
    Object.assign(this, entity)
  }
}

export abstract class BaseStore<TEntity extends Entity, TKey = number | string> {
  @observable loading = false
  @observable entities = observable.map<TKey, TEntity>()
  @observable totalCount = 0

  constructor() {
    makeObservable(this)
  }

  @computed
  get size() {
    return this.entities.size
  }

  @computed
  get list() {
    return [...this.entities.values()]
  }

  getEntity = (entityId: TKey) => {
    return this.entities.get(entityId)
  }

  @action
  setLoading(value: boolean) {
    this.loading = value
  }

  @action
  setTotalCount(totalCount: number | undefined) {
    if (totalCount !== undefined) {
      this.totalCount = totalCount
    }
  }

  @action
  putEntity(entity: TEntity | TEntity[]) {
    const entities = entity instanceof Array ? entity : [entity]

    for (const ent of entities) {
      this.entities.set(this.getKey(ent), ent)
    }
  }

  /**
   * заменяет все сущности в хранилище на те, которые переданы в массиве
   *
   * @param {BaseEntityInstance[]} entities - новые сущности. Только они будут в хранилище
   */
  @action
  putEntities(entities: TEntity[]) {
    this.entities.clear()
    this.putEntity(entities)
  }

  @action
  patchEntity(entityId: TKey, patch: Omit<Partial<TEntity>, 'id'>) {
    const entity = this.entities.get(entityId)

    if (!entity) {
      return
    }

    Object.assign(entity, patch)

    this.entities.set(entityId, entity)
  }

  @action
  removeEntity(entityId: TKey) {
    this.entities.delete(entityId)
  }

  @action
  clear() {
    this.entities.clear()
  }

  protected isEqual(obj1: object, obj2: object) {
    return getHashCode(obj1) === getHashCode(obj2)
  }

  protected getKey(obj: TEntity): TKey {
    return obj.id as unknown as TKey
  }
}
